import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { PublicCatalogItem } from "@/types";

export interface PublicCatalogConfig {
  show_prices: boolean;
  show_stock: boolean;
  show_whatsapp: boolean;
  whatsapp_number: string | null;
}

export interface PublicCompanyInfo {
  company_name: string | null;
  logo_url: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  address: string | null;
}

const DEFAULT_CONFIG: PublicCatalogConfig = {
  show_prices: true,
  show_stock: false,
  show_whatsapp: true,
  whatsapp_number: null,
};

export interface PublicCatalogData {
  items: PublicCatalogItem[];
  config: PublicCatalogConfig;
  company: PublicCompanyInfo | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Carga el catálogo público desde las vistas seguras (solo productos visibles y activos).
 * No requiere iniciar sesión.
 */
export function usePublicCatalog(): PublicCatalogData {
  const [items, setItems] = useState<PublicCatalogItem[]>([]);
  const [config, setConfig] = useState<PublicCatalogConfig>(DEFAULT_CONFIG);
  const [company, setCompany] = useState<PublicCompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [itemsRes, configRes, companyRes] = await Promise.all([
        supabase.from("public_catalog_items").select("*").order("name", { ascending: true }),
        supabase
          .from("catalog_settings")
          .select("show_prices, show_stock, show_whatsapp, whatsapp_number")
          .limit(1)
          .maybeSingle(),
        supabase
          .from("public_company_info")
          .select("company_name, logo_url, phone, whatsapp, email, address")
          .limit(1)
          .maybeSingle(),
      ]);

      if (itemsRes.error) {
        setError("No se pudo cargar el catálogo. Intenta de nuevo.");
        setItems([]);
        return;
      }

      setItems((itemsRes.data ?? []) as PublicCatalogItem[]);

      if (configRes.data) {
        const data = configRes.data;
        setConfig({
          show_prices: data.show_prices ?? true,
          show_stock: data.show_stock ?? false,
          show_whatsapp: data.show_whatsapp ?? true,
          whatsapp_number: data.whatsapp_number ?? null,
        });
      }

      setCompany((companyRes.data ?? null) as PublicCompanyInfo | null);
    } catch {
      setError("No se pudo cargar el catálogo. Intenta de nuevo.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { items, config, company, loading, error, reload: load };
}